import React, { useState, useEffect, useCallback } from "react";
import { Search, Loader2, AlertCircle, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { pingAPI } from "@/lib/ping-api";
import type { User } from "@/types";

// Helper function to get user initials
const getUserInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Helper function to get avatar gradient
const getAvatarGradient = (name: string) => {
  const gradients = [
    "from-blue-500 to-blue-600",
    "from-emerald-500 to-emerald-600",
    "from-violet-500 to-violet-600",
    "from-amber-500 to-amber-600",
    "from-rose-500 to-rose-600",
    "from-cyan-500 to-cyan-600",
    "from-indigo-500 to-indigo-600",
    "from-fuchsia-500 to-fuchsia-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

// Debounced search hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface AddPatientChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientSelect: (userId: string) => void;
}

export const AddPatientChatDialog = React.memo(
  ({ isOpen, onClose, onPatientSelect }: AddPatientChatDialogProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [patients, setPatients] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Fetch patients based on search query
    const fetchPatients = useCallback(async (query: string = "") => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await pingAPI.users.searchPatients(query);
        setPatients(result || []);
      } catch (err) {
        console.error("Error fetching patients:", err);
        setError("Failed to load patients");
        setPatients([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    // Reset state when dialog opens and fetch initial patients
    useEffect(() => {
      if (isOpen) {
        setSearchQuery("");
        setError(null);
        fetchPatients("");
      }
    }, [isOpen, fetchPatients]);

    // Fetch patients when search query changes (but not on initial open)
    useEffect(() => {
      if (isOpen && searchQuery !== "") {
        fetchPatients(debouncedSearchQuery);
      }
    }, [debouncedSearchQuery, isOpen, fetchPatients, searchQuery]);

    // Handle patient selection
    const handlePatientSelect = useCallback(
      async (patient: User) => {
        try {
          setIsLoading(true);

          // Create new chat for selected user
          const newChat = await pingAPI.chats.create({ user: patient.id });

          // Notify parent component
          onPatientSelect(newChat.id);

          // Close dialog
          onClose();
        } catch (err) {
          console.error("Error creating chat:", err);
          setError("Failed to create chat");
        } finally {
          setIsLoading(false);
        }
      },
      [onPatientSelect, onClose]
    );

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 pb-3 border-b border-border">
            <div className="flex items-center justify-between">
              <DialogTitle className="z-h2 flex items-center gap-2 text-foreground">
                <UserPlus className="h-5 w-5 text-primary" />
                New Patient Chat
              </DialogTitle>
            </div>
          </DialogHeader>

          {/* Search */}
          <div className="p-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
              <Input
                placeholder="Search patients by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="z-body h-10 pl-9"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Patient List */}
          <div className="max-h-[400px] overflow-y-auto px-4 pb-4">
            {error && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <span className="z-body mb-3 text-destructive">{error}</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="z-button"
                  onClick={() => fetchPatients(debouncedSearchQuery)}
                >
                  Try Again
                </Button>
              </div>
            )}

            {isLoading && patients.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 space-y-3">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="z-body text-muted-foreground">
                  Searching patients...
                </span>
              </div>
            )}

            {!isLoading && !error && patients.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <Search className="h-6 w-6 text-muted-foreground/50" />
                </div>
                <p className="z-body-md mb-1 text-foreground">
                  {debouncedSearchQuery
                    ? "No patients found"
                    : "No patients available"}
                </p>
                <p className="z-caption text-muted-foreground">
                  {debouncedSearchQuery
                    ? "Try a different search term"
                    : "Patient list is empty"}
                </p>
              </div>
            )}

            {patients.length > 0 && (
              <div className="space-y-1">
                {patients.map((patient) => {
                  const patientName = `${patient.first_name} ${patient.last_name}`;
                  const initials = getUserInitials(
                    patient.first_name,
                    patient.last_name
                  );

                  return (
                    <div
                      key={patient.id}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-all duration-150",
                        "border border-transparent hover:border-border hover:bg-muted/50"
                      )}
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "z-button flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-sm",
                            getAvatarGradient(patientName)
                          )}
                        >
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="z-body-md truncate text-foreground">
                                {patientName}
                              </h3>
                              <p className="z-secondary mt-0.5 text-muted-foreground">
                                {patient.age || "-"}y •{" "}
                                {patient.gender === "M"
                                  ? "Male"
                                  : patient.gender === "F"
                                    ? "Female"
                                    : patient.gender || "-"}
                                {patient.city && ` • ${patient.city}`}
                              </p>
                            </div>
                            {patient.zarity_user_id && (
                              <Badge
                                variant="secondary"
                                className="z-caption shrink-0"
                              >
                                {patient.zarity_user_id}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-3 text-muted-foreground">
                            {patient.phone_number && (
                              <span className="z-caption z-numeric">
                                {patient.phone_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

AddPatientChatDialog.displayName = "AddPatientChatDialog";
